import { environment } from '../../environments/environment';
import { Observable, of, throwError } from 'rxjs';
import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Cookie } from 'ng2-cookies';
import { IProject, IPRoductionOffice, IProductionContact, IProjectContact, IProjectStatus } from './project.model';
import { IUserInfo, AuthService } from '../shared/core/index';
import { DatePipe } from '@angular/common';
import { ILookup, LookupList } from '../shared/lookup';
import { CIDocument } from "../clearance-item/clearance-item.model";
import { HttpService } from '../shared/core/http.service';
import { map, tap, catchError } from 'rxjs/operators';

//declare var apiSessionId: any;

@Injectable()
export class ProjectService {

    private _userInfo: IUserInfo;    
    private _lookupsAPIUrl = new URL('lookups', environment.apiUrl).toString();
    private _projectAPIUrl = new URL('project', environment.apiUrl).toString();

    titles: ILookup[];
    tags: ILookup[];

    constructor(private _httpService: HttpService, private authService: AuthService) {
        this.authService.subscribeToLoginService().subscribe(r => { this._userInfo = r });
    }

    getProjects(id: string = '/'): Observable<IProject[]> {

        var url: string;
        
        if (id) {
            url = new URL(id, this._projectAPIUrl + '/').toString();
        }

            return this._httpService.httpGet(url).pipe(
                map((response) => {
                    return this.mapProjects(response);
                }),
                tap(data => data),
                catchError(this.handleError)
            );
    }

    getProject(id: string): Observable<IProject> {

        return this.getProjects(id).pipe(
            map((projects: IProject[]) => {
                return projects.find(p => p.id === id)
            }),
            catchError(this.handleError));
    }

    getLookups(department: string): Observable<LookupList[]> {

        
        return this._httpService.httpGet(this._lookupsAPIUrl + '?lookupTypes=titles,Tags' + (department ? '&department=' + department : '')).pipe(
            map((response: any) => response as LookupList[]),
            tap(data => data),
            catchError(this.handleError)
        );
    }

    saveProject(project: IProject): Observable<IProject> {
        
        if (project.id) {
            return this.updateProject(project);
        }
        return this.createProject(project);
    }

    createProject(project: IProject): Observable<IProject> {

        var projectCopy: IProject;

        projectCopy = JSON.parse(JSON.stringify(project));

        var body = JSON.stringify(projectCopy);
        
        if(body.indexOf("<") !== -1 || body.indexOf(">") !== -1){
            console.log('AntiXssMiddleware angled brackets ("<", ">") in your input(s).');
            return throwError(() => "AntiXssMiddleware");
        }
        
        return this._httpService.httpPost(this._projectAPIUrl, body).pipe(
            map((p: any) => this.toProject(p)),
            catchError(this.handleError)
        );

    }

    updateProject(project: IProject): Observable<IProject> {

        var projectCopy: IProject;

        projectCopy = JSON.parse(JSON.stringify(project));

        var body = JSON.stringify(projectCopy);
        
        if(body.indexOf("<") !== -1 || body.indexOf(">") !== -1){
            return throwError(() => "AntiXssMiddleware");
        }

        return this._httpService.httpPut(this._projectAPIUrl, body).pipe(
            map((p: any) => this.toProject(p)),
            catchError(this.handleError)
        );

    }

    deleteProject(id: string): Observable<boolean> {
        
        if (!id) {
            return of(false);
        }

        let url = new URL(id, this._projectAPIUrl + '/').toString();

        return this._httpService.httpDelete(url).pipe(
            map((p) => {
                return true;
            }),
            catchError(this.handleError)
        );

    }

    mapProjects(response: any): IProject[] {

        let body = response;

        this.titles = body.Lookups.Titles.map(t => this.toLookup(t));
        this.tags = body.Lookups.Tags.map(t => this.toLookup(t));

        var ret = body.Projects.map(r => this.toProject(r));
        return ret;
    }

    toLookup(t: any): ILookup {

        let i = new ILookup(t.id, t.name);
        return i;
    }

    toProject(r: any): IProject {

        let project = <IProject>
            ({
                id: r.Id,
                name: r.Name,
                keyCode: r.KeyCode,
                wprBillingCode: r.WprBillingCode,
                comments: r.Comments,
                wrapDate: r.WrapDate,
                releaseDate: r.ReleaseDate,
                startDate: r.StartDate,
                defaultProductionOffice: r.DefaultProductionOffice,
                defaultProductionContact: r.DefaultProductionContact,
                defaultProjectContact: r.DefaultProjectContact,
                department: r.Department,
                customStatus: r.CustomStatus,
                division: r.Division
            });
        project.productionOffices = new Array<IPRoductionOffice>();

        if (r.ProductionOffices) {
            r.ProductionOffices.forEach(element => {
                var po: IPRoductionOffice = new IPRoductionOffice();
                po.id = element.Id;
                po.entity = element.Entity;
                po.address = element.Address;
                po.phoneNumber = element.PhoneNumber;
                project.productionOffices.push(po);
            });
        }

        project.projectStatuses = new Array<IProjectStatus>();
        if(r.ProjectStatuses){
            r.ProjectStatuses.forEach(element => {
                var status: IProjectStatus = new IProjectStatus();
                status.statusId = element.StatusId;
                status.statusName = element.StatusName;
                status.statusGroup = element.StatusGroup;
                status.sortOrder = element.SortOrder;
                project.projectStatuses.push(status);
            });
        }

        //just in case nothing came from the service, let's default to the first one
        if ((!project.defaultProductionOffice) && project.productionOffices.length > 0) {
            project.defaultProductionOffice = project.productionOffices[0].id;
        }

        project.productionContacts = new Array<IProductionContact>();

        if (r.ProductionContacts) {
            r.ProductionContacts.forEach(element => {
                var po: IProductionContact = new IProductionContact();
                po.id = element.Id;
                po.email = element.Email;
                po.phoneNumber = element.PhoneNumber;
                po.phoneNumber2 = element.PhoneNumber2;
                po.phoneNumber3 = element.PhoneNumber3;                                
                po.name = element.Name;
                po.title = element.Title;
                project.productionContacts.push(po);
            });
        }

        //just in case nothing came from the service, let's default to the first one
        if ((!project.defaultProductionContact) && project.productionContacts.length > 0) {
            project.defaultProductionContact = project.productionContacts[0].id;
        }

        project.projectContacts = new Array<IProjectContact>();
        ;
        if (r.ProjectContacts) {
            r.ProjectContacts.forEach(element => {
                var po: IProjectContact = new IProjectContact();
                po.id = element.Id;
                po.name = element.Name;
                po.title = element.Title;
                po.email=element.Email;
                po.phoneNumber = element.PhoneNumber;
                po.emailNotification = element.EmailNotification;
                po.tags = element.Tags;
                project.projectContacts.push(po);
            });
        }

        //just in case nothing came from the service, let's default to the first one
        if ((!project.defaultProjectContact) && project.projectContacts.length > 0) {
            project.defaultProjectContact = project.projectContacts[0].id;
        }

        project.documents = new Array<CIDocument>();
        if (r.Documents) {
            r.Documents.forEach(element => {
                var doc: CIDocument = <CIDocument>(
                    {
                        id: element.Id,
                        name: element.Name,
                        fileType: element.FileType,
                        urlName: element.UrlName,
                        dMNumber: element.DMNumber,
                        cIId: element.CIId,
                        savedId: element.Id,
                        loading: false,
                        fullUrl: element.FullUrl,
                        url: element.Url
                    });
                project.documents.push(doc);
            });
        }

        return project;
    }

    getFilters(): any {
        return {};
    }

    private handleError(error: any) {
        return throwError(() => error || 'Server error');
    }
}
